import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ImagePlus, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const fieldCls = "rounded-xl border-2 border-monster-navy bg-white px-3 py-2 text-sm font-semibold shadow-sticker-sm";

export const AdminInventory = () => {
    const [drafts, setDrafts] = useState({});
    const [saving, setSaving] = useState("");
    const [uploading, setUploading] = useState("");
    const fileInputs = useRef({});

    const load = () => {
        api.get("/inflatables")
            .then((res) => setDrafts(Object.fromEntries(res.data.map((i) => [i.id, { ...i }]))))
            .catch((err) => toast.error(formatApiError(err)));
    };

    useEffect(load, []);

    const update = (id, key, value) => setDrafts((d) => ({ ...d, [id]: { ...d[id], [key]: value } }));

    const save = async (id) => {
        setSaving(id);
        const d = drafts[id];
        try {
            await api.patch(`/inflatables/${id}`, {
                name: d.name,
                tag: d.tag,
                description: d.description,
                price: Number(d.price),
                dimensions: d.dimensions,
                capacity: d.capacity,
                image: d.image,
            });
            toast.success(`${d.name} saved — live on the site now`);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving("");
        }
    };

    const upload = async (id, file) => {
        if (!file) return;
        setUploading(id);
        try {
            const formData = new FormData();
            formData.append("file", file);
            const res = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
            update(id, "image", res.data.url);
            toast.success("Photo uploaded — hit Save Changes to publish it");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploading("");
        }
    };

    const ids = Object.keys(drafts);
    if (ids.length === 0) {
        return <p className="rounded-3xl border-2 border-monster-navy bg-white p-10 text-center font-semibold text-monster-navy/50 shadow-sticker">Loading inventory...</p>;
    }

    return (
        <div className="grid gap-8 lg:grid-cols-2">
            {ids.map((id, i) => {
                const d = drafts[id];
                return (
                    <motion.div
                        key={id}
                        data-testid={`admin-inventory-card-${id}`}
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.06, type: "spring", stiffness: 100, damping: 15 }}
                        className="overflow-hidden rounded-3xl border-2 border-monster-navy bg-white shadow-sticker"
                    >
                        <div className="relative">
                            <img src={d.image} alt={d.name} className="h-48 w-full object-cover" />
                            <button
                                data-testid={`admin-inv-upload-${id}`}
                                onClick={() => fileInputs.current[id]?.click()}
                                disabled={uploading === id}
                                className="absolute bottom-3 right-3 inline-flex items-center gap-2 rounded-full border-2 border-monster-navy bg-monster-yellow px-4 py-2 font-display text-xs font-semibold text-monster-navy shadow-sticker-sm transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                            >
                                <ImagePlus className="h-4 w-4" /> {uploading === id ? "Uploading..." : "Swap Photo"}
                            </button>
                            <input
                                ref={(el) => (fileInputs.current[id] = el)}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                data-testid={`admin-inv-file-${id}`}
                                onChange={(e) => upload(id, e.target.files[0])}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4 p-6">
                            <div>
                                <Label className="mb-1.5 block font-display text-xs font-semibold text-monster-navy/60">Name</Label>
                                <Input data-testid={`admin-inv-name-${id}`} value={d.name} onChange={(e) => update(id, "name", e.target.value)} className={fieldCls} />
                            </div>
                            <div>
                                <Label className="mb-1.5 block font-display text-xs font-semibold text-monster-navy/60">Tag</Label>
                                <Input data-testid={`admin-inv-tag-${id}`} value={d.tag} onChange={(e) => update(id, "tag", e.target.value)} className={fieldCls} />
                            </div>
                            <div>
                                <Label className="mb-1.5 block font-display text-xs font-semibold text-monster-navy/60">Price ($/day)</Label>
                                <Input data-testid={`admin-inv-price-${id}`} type="number" min="0" value={d.price} onChange={(e) => update(id, "price", e.target.value)} className={fieldCls} />
                            </div>
                            <div>
                                <Label className="mb-1.5 block font-display text-xs font-semibold text-monster-navy/60">Dimensions</Label>
                                <Input data-testid={`admin-inv-dimensions-${id}`} value={d.dimensions} onChange={(e) => update(id, "dimensions", e.target.value)} className={fieldCls} />
                            </div>
                            <div className="col-span-2">
                                <Label className="mb-1.5 block font-display text-xs font-semibold text-monster-navy/60">Capacity</Label>
                                <Input data-testid={`admin-inv-capacity-${id}`} value={d.capacity} onChange={(e) => update(id, "capacity", e.target.value)} className={fieldCls} />
                            </div>
                            <div className="col-span-2">
                                <Label className="mb-1.5 block font-display text-xs font-semibold text-monster-navy/60">Description</Label>
                                <Textarea data-testid={`admin-inv-description-${id}`} rows={2} value={d.description} onChange={(e) => update(id, "description", e.target.value)} className={fieldCls} />
                            </div>
                            <div className="col-span-2">
                                <button
                                    data-testid={`admin-inv-save-${id}`}
                                    onClick={() => save(id)}
                                    disabled={saving === id}
                                    className="inline-flex w-full items-center justify-center gap-2 rounded-full border-2 border-monster-navy bg-monster-green py-3 font-display text-sm font-semibold text-monster-navy shadow-sticker-sm transition-all hover:-translate-y-0.5 active:translate-y-1 active:shadow-none disabled:opacity-60"
                                >
                                    <Save className="h-4 w-4" /> {saving === id ? "Saving..." : "Save Changes"}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                );
            })}
        </div>
    );
};
